import { Flex, Text } from '@components';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { ChartTooltip } from '@components/Charts/common/ChartTooltip/ChartTooltip';
import { useTheme, Box, lighten } from '@mui/material';
import moment from 'moment';
import { FormattedMessage, useIntl } from 'react-intl';
import { NivoGridCard } from '@components/Charts/common/NivoGridContainer';
import { historicalPerformancePlaceholder } from './Overview.consts';
import {
  InfoCardsContainer,
  StyledCard,
  ChartsContainer,
  HistoricalChartContainer,
  QuarterlyUpdateTextContainer,
} from './Overview.styles';
import { Config } from '@config/config';
import { BarDatum, BarTooltipProps } from '@nivo/bar';
import CalculatorBarChart from '@components/Charts/CalculatorLineChart/CalculatorBarChart';
import TextToolTip from '@components/TextToolTip/TextToolTip';
import { InvestmentData as InvestmentDataResponse } from '@features/api/response/UserPortfolio';
import { getTranslation } from '@utils/intl/intlUtils';
import { useMediaQueries } from '@utils/mediaQueries/mediaQueries';
import { useMemo } from 'react';

export interface InvestmentDataProps {
  data?: InvestmentDataResponse;
  currency?: string;
}

const InvestmentData: React.FC<InvestmentDataProps> = ({ data, currency }) => {
  const intl = useIntl();
  const theme = useTheme();
  const { sm: isMobile } = useMediaQueries();

  const getChangeColor = (value: number | undefined) => {
    if (!value || Number(value) === 0 || Number(value) < 0)
      return theme.palette.black.A100;

    return theme.palette.success.main;
  };

  const getChangeSign = (value: number | undefined) => {
    if (!value || Number(value) === 0) return '';

    return value > 0 ? '+' : '';
  };

  const getTooltipAnchor = (index: number) => {
    if (index === 0) return 'left';

    return index === (data?.historicalPerformanceChart?.y?.length ?? 0) - 1
      ? 'right'
      : undefined;
  };

  const hasHistoricalPerformanceData =
    data?.historicalPerformanceChart.x.length;

  const asOfText = `${getTranslation(intl, 'portfolio.asOf')} ${moment(
    data?.historicalPerformanceChart.x[
      data?.historicalPerformanceChart.x.length - 1
    ]
  ).format('MMMM YYYY')}`;

  const [chartData, hasLoss] = useMemo(() => {
    const hasAnyLoss = !!data?.historicalPerformanceChart.x.find(
      (_xAxis, i) => data?.historicalPerformanceChart.y[i].netGain < 0
    );

    return [
      !hasHistoricalPerformanceData
        ? historicalPerformancePlaceholder
        : data?.historicalPerformanceChart.x.map((xAxis, i) => {
            const netGain = data?.historicalPerformanceChart.y[i].netGain;
            const hasLoss = netGain < 0;
            let result: any = {
              x: moment(xAxis).format('MMM YYYY'),
              investedCapital:
                (netGain ?? 0) < 0
                  ? data?.historicalPerformanceChart.y[i].investedCapital +
                    netGain
                  : data?.historicalPerformanceChart.y[i].investedCapital,
              netGain: !hasLoss ? netGain : 0,
              deployedCapital:
                data?.historicalPerformanceChart.y[i].deployedCapital,
            };

            if (hasLoss) {
              result = { ...result, netLoss: Math.abs(netGain) };
            }

            return result;
          }),
      hasAnyLoss,
    ];
  }, [data, hasHistoricalPerformanceData]);

  const keys = useMemo(() => {
    const result = Object.keys(chartData[0]).slice(
      1,
      Object.keys(chartData[0]).length
    );
    if (hasLoss) {
      result.push('netLoss');
    }

    return result;
  }, [chartData, hasLoss]);

  const { sortedColors } = useMemo(() => {
    const sortedColors = keys.map((x, index) => {
      if (x === 'netLoss') {
        return theme.palette.grey[500];
      }

      return lighten(theme.palette.primary.main, (1 / keys.length) * index);
    });
    return { sortedColors: sortedColors };
  }, [keys, theme.palette]);

  return (
    <>
      <InfoCardsContainer>
        <StyledCard title={getTranslation(intl, 'portfolio.investmentAmount')}>
          <Text variant='h4' fontWeight='fontWeightBold'>
            {intl.formatNumber(data?.subscribedCapital ?? 0, {
              style: 'currency',
              currency: currency ?? data?.currencyCode ?? Config.currency.code,
              maximumFractionDigits: 2,
            })}
          </Text>
        </StyledCard>
        <StyledCard title={getTranslation(intl, 'portfolio.currentValue')}>
          <Flex alignItems='center' gap={3}>
            <Text variant='h4' fontWeight='fontWeightBold'>
              {intl.formatNumber(data?.totalValue ?? 0, {
                style: 'currency',
                currency:
                  currency ?? data?.currencyCode ?? Config.currency.code,
                maximumFractionDigits: 2,
              })}
            </Text>
          </Flex>
        </StyledCard>
        <StyledCard
          title={getTranslation(intl, 'portfolio.navPerShare')}
          subtitle={asOfText}
        >
          <Flex alignItems='center' gap={3}>
            <Text variant='h4' fontWeight='fontWeightBold'>
              {intl.formatNumber(data?.navPerShare ?? 0, {
                style: 'currency',
                currency:
                  currency ?? data?.currencyCode ?? Config.currency.code,
                maximumFractionDigits: 2,
              })}
            </Text>
          </Flex>
        </StyledCard>
        <StyledCard
          title={getTranslation(intl, 'portfolio.totalReturn')}
          subtitle={asOfText}
          tooltipText={getTranslation(intl, 'portfolio.onInvestedCapital')}
        >
          <Text
            variant='h4'
            fontWeight='fontWeightBold'
            sx={{ color: getChangeColor(data?.totalReturn) }}
          >
            {getChangeSign(data?.totalReturn)}
            {intl.formatNumber((data?.totalReturn ?? 0) / 100, {
              style: 'percent',
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
            })}
          </Text>
        </StyledCard>
        <StyledCard
          title={getTranslation(intl, 'portfolio.quarterlyReturn')}
          subtitle={asOfText}
          tooltipText={getTranslation(intl, 'portfolio.onInvestedCapital')}
        >
          <Text
            variant='h4'
            fontWeight='fontWeightBold'
            sx={{ color: getChangeColor(data?.quarterlyReturn) }}
          >
            {getChangeSign(data?.quarterlyReturn)}
            {intl.formatNumber((data?.quarterlyReturn ?? 0) / 100, {
              style: 'percent',
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
            })}
          </Text>
        </StyledCard>
      </InfoCardsContainer>
      <ChartsContainer
        sx={{
          height: { xs: 450, md: 'auto' },
        }}
      >
        <HistoricalChartContainer>
          {!hasHistoricalPerformanceData ? (
            <QuarterlyUpdateTextContainer>
              <Text variant='h5' fontWeight='fontWeightMedium'>
                <FormattedMessage id='portfolio.fundPerformanceUpdatedQuarterly' />
              </Text>
            </QuarterlyUpdateTextContainer>
          ) : null}
          <NivoGridCard
            sx={{
              height: { xs: 450, md: 'auto' },
            }}
          >
            <Flex
              sx={{
                flex: 1,
                pointerEvents: !hasHistoricalPerformanceData ? 'none' : 'auto',
                filter: !hasHistoricalPerformanceData ? 'blur(5px)' : 'none',
              }}
            >
              <Flex
                sx={{
                  flex: 1,
                  height: { xs: 450, md: 380 },
                }}
              >
                <CalculatorBarChart
                  legendTranslationId='portfolio'
                  data={chartData}
                  keys={keys}
                  colors={sortedColors}
                  indexBy='x'
                  valueScale={{
                    type: 'linear',
                  }}
                  axisBottom={{
                    tickRotation: isMobile ? -30 : 0,
                    tickPadding: isMobile ? 10 : 0,
                  }}
                  axisLeft={{
                    format: (v) =>
                      intl.formatNumber(v, {
                        style: 'currency',
                        currency:
                          currency ??
                          data?.currencyCode ??
                          Config.currency.code,
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 1,
                        notation: 'compact',
                        compactDisplay: 'short',
                      }),
                  }}
                  margin={{
                    right: isMobile ? 20 : 40,
                    bottom: isMobile ? 80 : 30,
                    top: 30,
                    left: 65,
                  }}
                  tooltip={(pointData: BarTooltipProps<BarDatum>) => (
                    <Box mb={16}>
                      <ChartTooltip
                        title={pointData.indexValue}
                        anchor={getTooltipAnchor(pointData.index)}
                        items={[
                          {
                            label: getTranslation(
                              intl,
                              'portfolio.investedCapital'
                            ),
                            value: intl.formatNumber(
                              data?.historicalPerformanceChart.y[
                                pointData.index
                              ].investedCapital ?? 0,
                              {
                                style: 'currency',
                                currency:
                                  currency ??
                                  data?.currencyCode ??
                                  Config.currency.code,
                                maximumFractionDigits: 2,
                              }
                            ),
                          },
                          {
                            label: getTranslation(
                              intl,
                              (data?.historicalPerformanceChart.y[
                                pointData.index
                              ].netGain ?? 0) >= 0
                                ? 'portfolio.netGain'
                                : 'portfolio.netLoss',
                              {}
                            ),
                            value: intl.formatNumber(
                              data?.historicalPerformanceChart.y[
                                pointData.index
                              ].netGain ?? 0,
                              {
                                style: 'currency',
                                currency:
                                  currency ??
                                  data?.currencyCode ??
                                  Config.currency.code,
                                maximumFractionDigits: 2,
                              }
                            ),
                          },
                          {
                            label: getTranslation(
                              intl,
                              'portfolio.deployedCapital'
                            ),
                            value: intl.formatNumber(
                              data?.historicalPerformanceChart.y[
                                pointData.index
                              ].deployedCapital ?? 0,
                              {
                                style: 'currency',
                                currency:
                                  currency ??
                                  data?.currencyCode ??
                                  Config.currency.code,
                                maximumFractionDigits: 2,
                              }
                            ),
                          },
                        ]}
                      />
                    </Box>
                  )}
                  title={getTranslation(
                    intl,
                    'portfolio.historicalPerformance'
                  )}
                  headerRight={
                    <Flex
                      sx={{
                        gap: 4,
                        alignItems: 'center',
                        marginLeft: { xs: 10, sm: 0 },
                      }}
                    >
                      <Flex flexDirection='column' alignItems='flex-end'>
                        <Text
                          variant='body'
                          fontWeight='fontWeightSemiBold'
                          sx={{ color: theme.palette.grey.A100 }}
                        >
                          <FormattedMessage id='portfolio.valuationAsOf' />
                        </Text>
                        <Text variant='body' fontWeight='fontWeightSemiBold'>
                          {moment(
                            data?.historicalPerformanceChart.x[
                              data?.historicalPerformanceChart.x.length - 1
                            ]
                          ).format('MMMM YYYY')}
                        </Text>
                      </Flex>

                      <TextToolTip
                        text=''
                        toolTipText={
                          <Flex flexDirection='column' textAlign='left' gap={6}>
                            <Text variant='body' fontWeight='fontWeightMedium'>
                              <Text variant='body' fontWeight='fontWeightBold'>
                                <FormattedMessage id='portfolio.historicalChartTooltip.investedCapital' />
                              </Text>
                              <FormattedMessage id='portfolio.historicalChartTooltip.investedCapitalDescription' />
                            </Text>
                            <Text variant='body' fontWeight='fontWeightMedium'>
                              <Text variant='body' fontWeight='fontWeightBold'>
                                <FormattedMessage id='portfolio.historicalChartTooltip.netGain' />
                              </Text>
                              <FormattedMessage id='portfolio.historicalChartTooltip.netGainDescription' />
                            </Text>
                            <Text variant='body' fontWeight='fontWeightMedium'>
                              <Text variant='body' fontWeight='fontWeightBold'>
                                <FormattedMessage id='portfolio.historicalChartTooltip.deployedCapital' />
                              </Text>
                              <FormattedMessage id='portfolio.historicalChartTooltip.deployedCapitalDescription' />
                            </Text>
                          </Flex>
                        }
                        icon={() => (
                          <ErrorOutlineRoundedIcon
                            sx={{ color: theme.palette.grey[700] }}
                          />
                        )}
                      />
                    </Flex>
                  }
                />
              </Flex>
            </Flex>
          </NivoGridCard>
        </HistoricalChartContainer>
      </ChartsContainer>
    </>
  );
};

export default InvestmentData;
